// frontend/src/AppContent.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import "./App.css";
import { Helmet } from 'react-helmet-async';
import Header from "./components/Header/Header";
import WorkflowSelector from './components/WorkflowSelector/WorkflowSelector';
import Selector from "./components/Selector/Selector";
import OnboardingModal from './components/OnboardingModal/OnboardingModal';
import ContentInput from "./components/ContentInput/ContentInput";
import LoadingIndicator from "./components/LoadingIndicator/LoadingIndicator";
import ResultDisplay from "./components/ResultDisplay/ResultDisplay";
import Footer from "./components/Footer/Footer";
import ErrorMessage from './components/ErrorMessage/ErrorMessage';
import HeatmapModal from './components/HeatmapModal/HeatmapModal';
import TextInspectionModal from './components/TextInspectionModal/TextInspectionModal';
import { AuthContext } from './contexts/AuthContext';
import { AnalysisContext } from './contexts/AnalysisContext';
import { HistoryContext } from './contexts/HistoryContext';
import { UIContext } from './contexts/UIContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { Button, Box, CircularProgress } from '@mui/material'
import GuestLimitModal from './components/GuestLimitModal/GuestLimitModal';
import AdCreativeTools from './components/AdCreativeTools/AdCreativeTools';
import GuestSnackbar from './components/GuestSnackbar/GuestSnackbar';

function AppContent() {
    const { t } = useTranslation();
    const { user, logout, fetchWithAuth } = useContext(AuthContext);
    const {
        analysisResults, 
        setAnalysisResults,
        isLoading, 
        setIsLoading,
        content, 
        contentType,
        contentPreviewUrl,
        tasks,
        includeRegions, 
        includeExplanation,
        error, 
        setError,
        contextSourceUrl, 
        contextPlatform, 
        contextUserNotes, 
        contentSourceUrl
    } = useContext(AnalysisContext);
    const { setHasFetchedHistory } = useContext(HistoryContext);
    const {
        setIsFaqModalOpen,
        heatmapModalData, 
        setHeatmapModalData,
        textInspectionModalData, 
        setTextInspectionModalData,
        isPageBlurred,
        isDocViewerOpen
    } = useContext(UIContext);

    const [workflowMode, setWorkflowMode] = useState('manual');
    const [loadingMessages, setLoadingMessages] = useState({});
    const [openGuestSnackbar, setOpenGuestSnackbar] = useState(false);
    const [isGuestLimitModalOpen, setIsGuestLimitModalOpen] = useState(false);
    const [isAdToolsVisible, setIsAdToolsVisible] = useState(false);
    const [suggestionData, setSuggestionData] = useState(null);
    const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
    const analysisWasSuccessful = Object.keys(analysisResults).length > 0 && !error;
    const canShowAdToolsButton = analysisWasSuccessful && !isLoading && !user?.is_guest && (contentType === 'image' || contentType === 'video');

    useEffect(() => {
        if (user?.is_guest) { setOpenGuestSnackbar(true); }
    }, [user?.is_guest]);

    const handleCloseGuestLimitModal = () => setIsGuestLimitModalOpen(false);

    // This effect will now be the single source of truth for showing the Ad Creative Tools.
    useEffect(() => {

        const isMedia = contentType === 'image' || contentType === 'video';

        if (analysisWasSuccessful && isMedia && !isLoading) {
            const fetchOrShowTools = async () => {
                // For video, we don't fetch suggestions, but we still might auto-show the tool.
                if (contentType === 'video') {
                    setSuggestionData(null); // Ensure no old image data is used
                    if (user?.show_ad_tools_by_default) {
                        setIsAdToolsVisible(true);
                    }
                    return;
                }

                // --- IMAGE LOGIC ---
                // 1. Show loading state immediately
                setIsFetchingSuggestion(true);
                setSuggestionData(null);

                // 2. If user wants tools auto-shown, reveal the container NOW.
                // This allows the spinner (which lives inside the visible container) to actually appear.
                if (user?.show_ad_tools_by_default) {
                    setIsAdToolsVisible(true);
                }

                try {
                    const formData = new FormData();
                    formData.append("image_file", content);
                    // Logic to determine default overlay type
                    // If there is a contentSourceUrl (meaning it came from a URL), default to 'shield'.
                    // Otherwise (local upload), default to 'qr'.
                    const defaultType = (contentSourceUrl && contentSourceUrl.trim() !== '') ? 'shield' : 'qr';
                    formData.append("overlay_type", defaultType);
                    const response = await fetchWithAuth("/api/media/placement-suggestion", { method: "POST", body: formData });
                    if (!response.ok) throw new Error("Failed to get placement suggestions.");
                    const data = await response.json();
                    setSuggestionData(data);

                } catch (err) {
                    console.error("Error fetching placement suggestions:", err);
                    setSuggestionData({ error: err.message }); // Pass error down to child
                } finally {
                    setIsFetchingSuggestion(false);
                }
            };

            fetchOrShowTools();

        } else {
            // Clear suggestions if the analysis was not successful or for a different content type
            setSuggestionData(null);
            // Hide the tools if a new analysis starts or if it fails
            if (isLoading || error) {
                setIsAdToolsVisible(false);
            }
        }
    }, [analysisResults, contentType, content, error, isLoading, fetchWithAuth, user?.show_ad_tools_by_default, analysisWasSuccessful, contentSourceUrl]);

    const handleSubmit = async (overrides = {}) => {
        if (!tasks || tasks.length === 0) {
            setError("Please select at least one analysis task.");
            return;
        }

        setIsLoading(true);
        setAnalysisResults({});
        setError(null);
        setLoadingMessages({
            general: { key: 'streaming.initiating' }
        });

        // Determine final context values: use override if present, otherwise use current state
        const finalSourceUrl = overrides.hasOwnProperty('contextSourceUrl') ? overrides.contextSourceUrl : contextSourceUrl;
        const finalPlatform = overrides.hasOwnProperty('contextPlatform') ? overrides.contextPlatform : contextPlatform;
        const finalUserNotes = overrides.hasOwnProperty('contextUserNotes') ? overrides.contextUserNotes : contextUserNotes;

        const formData = new FormData();
        formData.append("content", content);
        formData.append("type", contentType);
        if (contentSourceUrl) formData.append("original_source_url", contentSourceUrl);
        tasks.forEach(task => { formData.append("tasks[]", task); });
        formData.append("includeRegions", includeRegions.toString());
        formData.append("includeExplanation", includeExplanation.toString());
        if (finalSourceUrl) formData.append("context_source_url", finalSourceUrl);
        if (finalPlatform) formData.append("context_platform", finalPlatform);
        if (finalUserNotes) formData.append("context_user_notes", finalUserNotes);
        try {
            const response = await fetchWithAuth("/api/analyze", {
                method: 'POST',
                body: formData,
            });

            if (response.status === 429) {
                let errorData;
                try { errorData = await response.json(); } catch (e) { errorData = { error: "GENERIC_RATE_LIMIT" }; }
                if (errorData.error === 'GUEST_LIMIT_REACHED') { setIsGuestLimitModalOpen(true); }
                else { setError("You have made too many requests. Please wait a moment and try again."); }
                setIsLoading(false);
                return;
            }

            // --- Handle 402 Payment Required ---
            if (response.status === 402) {
                let errorData = {};
                try { errorData = await response.json(); } catch (e) {}
                
                let translatedMessage = errorData.message || "Payment Required";
                
                // Map the new backend error codes to your translation.json keys
                if (errorData.error === 'CANCELLATION_PENDING') {
                    // Uses the translation key that perfectly matches the text in your screenshot
                    translatedMessage = t('billingPage.cancellationPendingDesc', { defaultValue: errorData.message });
                } 
                else if (errorData.error === 'TRIAL_EXCEEDS') {
                    // Injects the dynamic math from the backend payload into your translation template
                    translatedMessage = t('appContent.trialBanner.exceeds', {
                        estimated: errorData.estimated,
                        remaining: errorData.remaining,
                        defaultValue: errorData.message
                    });
                } 
                else if (errorData.error === 'TRIAL_EXPIRED') {
                    translatedMessage = t('appContent.trialBanner.expired', { defaultValue: errorData.message });
                }

                setError(translatedMessage);
                setIsLoading(false);
                return;
            }

            if (!response.ok || !response.body) {
                let errorPayload = { error: `HTTP error! Status: ${response.status}` };
                try { errorPayload = await response.json(); } catch (e) { /* ignore */ }
                setError(errorPayload.message || errorPayload.error || "Unknown API error");
                setIsLoading(false);
                return;
            }

            // --- Local variables to track the outcome of the stream within this function's scope ---
            // This is crucial because React state updates are asynchronous.
            const currentAnalysisResults = {};

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let finalStreamEnded = false;
            
            // --- THE BULLETPROOF BUFFER ---
            let streamBuffer = '';

            if (tasks.length > 1) { setLoadingMessages({}); }

            while (!finalStreamEnded) {
                const { value, done } = await reader.read();
                if (done) break;

                // Safely decode and append incoming network bytes to our buffer
                streamBuffer += decoder.decode(value, { stream: true });

                // Process the buffer ONLY when a complete boundary (\n\n) is found
                let boundaryIndex;
                while ((boundaryIndex = streamBuffer.indexOf('\n\n')) !== -1) {
                    // Extract the complete message
                    const messageString = streamBuffer.substring(0, boundaryIndex);
                    // Remove the processed message from the buffer
                    streamBuffer = streamBuffer.substring(boundaryIndex + 2);

                    if (messageString.startsWith("data:")) {
                        const jsonData = messageString.substring(5).trim();
                        if (jsonData) {
                            try {
                                const eventData = JSON.parse(jsonData);

                                if (eventData.messageKey) {
                                    const translatedParams = { ...(eventData.messageParams || {}) };
                                    
                                    if (translatedParams.taskName) {
                                        translatedParams.taskName = t(`resultDisplay.analysisTasks.${translatedParams.taskName}`, { defaultValue: translatedParams.taskName });
                                    }
                                    if (translatedParams.taskKey) {
                                        translatedParams.taskKey = t(`resultDisplay.analysisTasks.${translatedParams.taskKey}`, { defaultValue: translatedParams.taskKey });
                                    }

                                    const messagePayload = { key: eventData.messageKey, params: translatedParams };
                                    const reliableTaskKey = eventData.task_name || translatedParams.taskKey;

                                    // --- 100% SAFE BATCHED STATE UPDATE ---
                                    setLoadingMessages(prev => {
                                        // Always clone the previous state so we never overwrite concurrent updates
                                        const newState = { ...prev };
                                        
                                        if (reliableTaskKey && tasks.length > 1) {
                                            // 1. Add/Update the specific task
                                            newState[reliableTaskKey] = messagePayload;
                                            // 2. Remove the generic "Initiating..." message if it exists
                                            delete newState.general; 
                                        } else { 
                                            // Generic messages (like "Hashing...")
                                            newState.general = messagePayload; 
                                        }
                                        
                                        return newState;
                                    });
                                }

                                if (eventData.status === "completed" && eventData.isFinal) {
                                    const reliableTaskKey = eventData.task_name || eventData.result?.task;
                                    
                                    setAnalysisResults(prev => ({ ...prev, [reliableTaskKey]: eventData.result }));
                                    currentAnalysisResults[reliableTaskKey] = eventData.result;
                                    
                                    // --- DON'T DELETE THE MESSAGE ---
                                    setLoadingMessages(prev => {
                                        const newState = { ...prev };
                                        
                                        // Try to get the translated name we previously stored, or fallback to the raw key
                                        const currentTaskName = prev[reliableTaskKey]?.params?.taskName || reliableTaskKey;
                                        
                                        // Update the specific task's message instead of deleting it
                                        newState[reliableTaskKey] = {
                                            // Use the key we just added to the JSON
                                            key: "streaming.taskCompletedWaiting", 
                                            params: { taskName: currentTaskName }
                                        };
                                        
                                        return newState;
                                    });
                                } else if (eventData.status === "error") {
                                    const errorPayload = { key: eventData.messageKey || 'errors.unknown', params: eventData.messageParams || {} };
                                    setError(errorPayload);
                                    setIsLoading(false);
                                    finalStreamEnded = true;
                                } else if (eventData.status === "stream_ended") {
                                    setIsLoading(false);
                                    finalStreamEnded = true;
                                    setHasFetchedHistory(false);
                                }
                            } catch (e) { 
                                console.error("Error parsing SSE JSON:", e, jsonData); 
                            }
                        }
                    }
                }
            }

        } catch (err) {
            setError(err.message || "A network error occurred.");
            setIsLoading(false);
        }
    };

    const shouldShowOutputContainer = isLoading || error || Object.keys(analysisResults).length > 0;
    const shouldBlurApp = isGuestLimitModalOpen || !!heatmapModalData || !!textInspectionModalData || isPageBlurred || isDocViewerOpen;

    return (
        <>
            <div className={`app-content-wrapper ${shouldBlurApp ? 'app-content-blurred' : ''}`}>
                <Helmet>
                    <title>Vetra AI</title>
                </Helmet>
                <Header showDataFlowPreview={true} />
                <main className="app-container">
                    <ErrorBoundary>
                        <div className="main-content">
                            <WorkflowSelector
                                activeMode={workflowMode}
                                setActiveMode={setWorkflowMode}
                            />
                            {workflowMode === 'manual' && (
                                <>
                                    <Selector />
                                    <ContentInput onSubmit={handleSubmit} isLoading={isLoading} />
                                    <div className={`analysis-output-container ${shouldShowOutputContainer ? 'active' : ''}`}>
                                        {error && <ErrorMessage message={error} />}

                                        {isLoading && <LoadingIndicator messages={loadingMessages} />}

                                        {!isLoading && Object.keys(analysisResults).length > 0 &&
                                            <ResultDisplay
                                                results={analysisResults}
                                                taskOrder={tasks}
                                            />
                                        }

                                        {canShowAdToolsButton && (
                                            <>
                                                <div className="post-analysis-actions">
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => setIsAdToolsVisible(prev => !prev)}
                                                        className="show-ad-tools-btn"
                                                    >
                                                        {isAdToolsVisible
                                                            ? t("adCreativeTools.hideButton")
                                                            : t("adCreativeTools.showButton")}
                                                    </Button>
                                                </div>

                                                {isAdToolsVisible && (
                                                    isFetchingSuggestion ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: 'transparent' }}>
                                                            <CircularProgress color="secondary" />
                                                        </Box>
                                                    ) : (
                                                        <AdCreativeTools
                                                            key={contentPreviewUrl} // This key forces a full reset when the input image changes
                                                            suggestionData={suggestionData}
                                                        />
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </ErrorBoundary>
                </main>
                <Footer
                    onOpenFaqModal={() => setIsFaqModalOpen(true)}
                    showFaqButton={true}
                />
            </div>

            <OnboardingModal />
            <GuestLimitModal isOpen={isGuestLimitModalOpen} onClose={handleCloseGuestLimitModal} onLogout={logout} />

            {heatmapModalData && (
                <HeatmapModal
                    isOpen={!!heatmapModalData}
                    onClose={() => setHeatmapModalData(null)}
                    imageSrc={heatmapModalData.imageSrc}
                    regions={heatmapModalData.regions}
                    taskName={heatmapModalData.taskName}
                    score={heatmapModalData.score}
                    mode={heatmapModalData.mode}
                    footerNote={heatmapModalData.footerNote}
                    contentType={heatmapModalData.contentType}
                />
            )}

            {textInspectionModalData && (
                <TextInspectionModal
                    isOpen={!!textInspectionModalData}
                    onClose={() => setTextInspectionModalData(null)}
                    text={textInspectionModalData.text}
                    regions={textInspectionModalData.regions}
                    taskName={textInspectionModalData.taskName}
                    score={textInspectionModalData.score}
                />
            )}

            <GuestSnackbar open={openGuestSnackbar} onClose={() => setOpenGuestSnackbar(false)} />
        </>
    );
}

export default AppContent;